import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ExtractionVerdict, SecondOpinion } from '~/graphql/graphql';
import {
  statusBadgeClasses,
  type StatusBadgeTone,
} from '~/components/ui/statusBadgeClasses';
import { EMPTY_CELL } from './importHandoff';

type Props = {
  verdict: ExtractionVerdict;
  claimedValue?: string | null;
  verifiedValue?: string | null;
  governingLabels?: string | null;
  secondOpinion?: SecondOpinion | null;
  secondOpinionReason?: string | null;
};

type ComparisonProps = Pick<
  Props,
  'verdict' | 'claimedValue' | 'verifiedValue' | 'governingLabels'
>;

const SAFE_VERDICT: ExtractionVerdict = ExtractionVerdict.Verified;

const OPINION_TONE: Partial<Record<SecondOpinion, StatusBadgeTone>> = {
  [SecondOpinion.Agrees]: 'shipped',
  [SecondOpinion.Disagrees]: 'declined',
};

export function VerdictBadge({
  verdict,
  claimedValue,
  verifiedValue,
  governingLabels,
  secondOpinion,
  secondOpinionReason,
}: Props) {
  const { t } = useTranslation('document-extraction');
  const isSafe = verdict === SAFE_VERDICT;
  const tone: StatusBadgeTone = isSafe ? 'accepted' : 'pending';

  return (
    <span className="inline-flex flex-col gap-1">
      <span
        data-testid="verdict-badge"
        data-safe={String(isSafe)}
        data-verdict={verdict}
        className={`w-fit ${statusBadgeClasses(tone)}`}
        title={
          verdict === ExtractionVerdict.Unverified
            ? t('verdict.UNVERIFIED_DESCRIPTION')
            : undefined
        }
      >
        {t(`verdict.${verdict}`)}
      </span>

      <VerdictComparison
        verdict={verdict}
        claimedValue={claimedValue}
        verifiedValue={verifiedValue}
        governingLabels={governingLabels}
      />

      {!isSafe && secondOpinion && (
        <SecondOpinionTag
          opinion={secondOpinion}
          reason={secondOpinionReason}
        />
      )}
    </span>
  );
}

function VerdictComparison(props: ComparisonProps) {
  const { t } = useTranslation('document-extraction');
  const comparison = comparisonFor(t, props);
  if (comparison == null) {
    return null;
  }

  return (
    <span data-testid="verdict-comparison" className="text-xs text-neut-sec">
      {comparison}
    </span>
  );
}

function comparisonFor(
  t: TFunction,
  { verdict, claimedValue, verifiedValue, governingLabels }: ComparisonProps
): ReactNode {
  switch (verdict) {
    case ExtractionVerdict.Contradicted:
      return (
        <>
          {t('verdict_comparison.extracted')}: {claimedValue ?? EMPTY_CELL} ·{' '}
          {governingLabels != null
            ? `${t('verdict_comparison.governed_by')}: ${governingLabels}`
            : `${t('verdict_comparison.found_in_source')}: ${verifiedValue ?? EMPTY_CELL}`}
        </>
      );
    case ExtractionVerdict.NoAnchor:
      return governingLabels == null ? null : (
        <>
          {t('verdict_comparison.governed_by')}: {governingLabels}
        </>
      );
    case ExtractionVerdict.NoCell:
      // A cell the detector never offered still holds a value; one that was invented does not.
      return verifiedValue == null ? null : (
        <>
          {t('verdict_comparison.not_offered')} ·{' '}
          {t('verdict_comparison.found_in_source')}: {verifiedValue}
        </>
      );
    default:
      return null;
  }
}

function SecondOpinionTag({
  opinion,
  reason,
}: {
  opinion: SecondOpinion;
  reason?: string | null;
}) {
  const { t } = useTranslation('document-extraction');
  if (opinion === SecondOpinion.Unavailable) {
    return null;
  }

  return (
    <span
      data-testid="second-opinion"
      data-opinion={opinion}
      title={reason ?? undefined}
      className={`w-fit ${statusBadgeClasses(OPINION_TONE[opinion] ?? 'info')}`}
    >
      {t(`second_opinion.${opinion}`)}
    </span>
  );
}
